import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, enableProdMode, importProvidersFrom, isDevMode } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';

import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FileUploadModule } from 'primeng/fileupload';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

import { ApiModule } from '@digitalforest/ngx-oak-api';

import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { apiConfigFactory, tokenProvider } from './app/app.module';
import { AuthInterceptor } from './app/auth.interceptor';
import { AuthService } from './app/auth.service';
import { environment } from './environments/environment';
import { version } from './environments/version';

import { JWT_OPTIONS, JwtModule } from '@auth0/angular-jwt';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { provideServiceWorker } from '@angular/service-worker';

if (environment.production) {
  enableProdMode();
}

// Only enable Sentry in production builds
if (environment.production && environment.SENTRY_DSN) {
  Sentry.init({
    dsn: environment.SENTRY_DSN,
    release: version.release,
    environment: 'production',
    sendDefaultPii: true,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    tracesSampleRate: 0.1,
    ignoreErrors: [
      // Ignore media track errors from scanner cleanup
      'Track has ended',
      'InvalidAccessError: Track has ended',
      // Ignore scanner race condition during cleanup - OAK-CHECKIN-SITE-R
      'scannerControls.stop',
      // Ignore scanner race condition during device changes - OAK-CHECKIN-SITE-C
      'No scanning is running at the time',
    ],
    beforeSend(event, hint) {
      // Ignore NotFoundException from QR code scanner - this is expected behavior
      if (hint.originalException && hint.originalException instanceof Error) {
        const error = hint.originalException;
        if (error.name === 'NotFoundException' ||
            error.message?.includes('No MultiFormat Readers were able to detect the code') ||
            error.message?.includes('setPhotoOptions failed') ||
            error.message?.includes('Track has ended') ||
            error.message?.includes('scannerControls.stop') ||
            error.message?.includes('No scanning is running at the time')) {
          return null; // Don't send to Sentry
        }
      }
      return event;
    },
  });
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      AppRoutingModule,
      BrowserModule,
      ButtonModule,
      CardModule,
      FileUploadModule,
      InputTextModule,
      ProgressBarModule,
      ProgressSpinnerModule,
      ReactiveFormsModule,
      ZXingScannerModule,
      ApiModule.forRoot(apiConfigFactory),
      JwtModule.forRoot({
        jwtOptionsProvider: {
          provide: JWT_OPTIONS,
          useFactory: tokenProvider,
          deps: [AuthService],
        },
      }),
    ),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => { /* Initialize Sentry TraceService */ },
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
          }),
  ],
}).catch((err) => console.error(err));
